<template>
    <span
        :class="['base-avatar', sizeClasses[size], appearanceClass, shape === 'square' ? 'rounded-lgr' : 'rounded-full']"
        :style="identityColor ? { backgroundColor: identityColor } : undefined">
        <slot>
            <span v-if="initials" :class="initialsTypography">{{ initials }}</span>
        </slot>
    </span>
</template>

<script lang="ts">
export type BaseAvatarTheme = "neutral" | "primary" | "success" | "warning" | "danger" | "identity";
export type BaseAvatarSize = "xs" | "sm" | "md" | "lg" | "xl";
export type BaseAvatarShape = "circle" | "square";

interface BaseAvatarProps {
    size?: BaseAvatarSize;
    theme?: BaseAvatarTheme;
    shape?: BaseAvatarShape;
    initials?: string;
    // Stable string (name, id) that picks this person's color from the perspective
    // palette. Only used with theme="identity"; falls back to `initials`.
    identityKey?: string;
}
</script>

<script setup lang="ts">
import { PERSPECTIVE_COLORS } from "~vue/utils/perspectivePalette";
import { computed } from "vue";

const { size = "md", theme = "neutral", shape = "circle", initials, identityKey } = defineProps<BaseAvatarProps>();

const sizeClasses: Record<BaseAvatarSize, string> = {
    xs: "w-7 h-7",
    sm: "w-8 h-8",
    md: "w-11 h-11",
    lg: "w-14 h-14",
    xl: "w-24 h-24",
};

const themeClasses: Record<Exclude<BaseAvatarTheme, "identity">, string> = {
    neutral: "bg-avatar-neutral-bg text-avatar-neutral-text",
    primary: "bg-avatar-primary-bg text-avatar-primary-text",
    success: "bg-avatar-success-bg text-avatar-success-text",
    warning: "bg-avatar-warning-bg text-avatar-warning-text",
    danger: "bg-avatar-danger-bg text-avatar-danger-text",
};

const appearanceClass = computed<string>(() => (theme === "identity" ? "text-white" : themeClasses[theme]));

// Decorative per-person color, stable for a given key.
const identityColor = computed<string | null>(() => {
    if (theme !== "identity") {
        return null;
    }
    const key = identityKey || initials || "";
    let hash = 0;
    for (const char of key) {
        hash = (hash * 31 + char.codePointAt(0)!) % PERSPECTIVE_COLORS.length ** 3;
    }
    return PERSPECTIVE_COLORS[hash % PERSPECTIVE_COLORS.length];
});

// Map each size to a single design-system typography utility that pins both
// font-size and font-weight, rather than layering a `font-semibold` override
// on top of a raw `text-*` size class. `title-body` is medium-weight (no
// `title-body-bold` exists in the DS); other sizes are semibold.
// Identity avatars use a serif initial (see scoped classes below).
const initialsTypography = computed<string>(() => {
    if (theme === "identity") {
        return size === "xs" || size === "sm" ? "initial-serif" : "initial-serif-lg";
    }
    switch (size) {
        case "xs":
        case "sm":
            return "body-small-bold";
        case "md":
            return "body-bold";
        case "lg":
            return "title-body";
        case "xl":
            return "display-sans-serif";
        default:
            return "body-bold";
    }
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .base-avatar {
        @apply inline-flex items-center justify-center shrink-0;
    }

    /* Identity initials are serif at sizes below the typography scale's smallest step. */
    .initial-serif {
        @apply font-serif font-bold text-[11px] leading-none;
    }

    .initial-serif-lg {
        @apply font-serif font-bold text-[16px] leading-none;
    }
}
</style>
